import axios from 'axios'
import { kratosActionUrl, type FlowKind, type KratosFlow } from './flows'

export type { KratosFlow, KratosNode, KratosMessage } from './flows'

export const apiClient = axios.create({
  baseURL: '/api',
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  withCredentials: true,
})

export interface SessionResponse {
  active: boolean
  verification_required?: boolean
  identity?: { id: string; email: string; username: string }
}

export interface HydraLoginResponse { redirect_to: string }
export interface HydraConsentResponse { redirect_to: string }

export const sessionApi = {
  checkSession: (signal?: AbortSignal) => apiClient.get<SessionResponse>('/session', { signal }),
}

export const hydraApi = {
  loginContext: (loginChallenge: string, signal?: AbortSignal) =>
    apiClient.get<{ email: string }>('/auth/hydra/login-context', { params: { login_challenge: loginChallenge }, signal }),
  acceptLogin: (loginChallenge: string, remember: boolean, signal?: AbortSignal) =>
    apiClient.post<HydraLoginResponse>('/auth/hydra/login', {
      login_challenge: loginChallenge, remember,
    }, { signal }),
  acceptConsent: (consentChallenge: string, grantScopes: string[], remember: boolean, allow: boolean) =>
    apiClient.post<HydraConsentResponse>('/auth/hydra/consent', {
      consent_challenge: consentChallenge, grant_scopes: grantScopes, remember, allow,
    }),
}

export const logoutApi = {
  getLogoutUrl: () => apiClient.get<{ logout_url: string }>('/logout'),
}

// Browser flows require their CSRF cookie, including when exchanging JSON.
const kratosClient = axios.create({
  baseURL: '/kratos',
  headers: { Accept: 'application/json' },
  withCredentials: true,
})

export const kratosPublicApi = {
  initializeFlow: (kind: FlowKind, returnTo?: string, refresh = false, aal?: string, signal?: AbortSignal) =>
    kratosClient.get<KratosFlow>(`/self-service/${kind}/browser`, {
      signal,
      params: {
        ...(returnTo ? { return_to: returnTo } : {}),
        ...(kind === 'login' && refresh ? { refresh: true } : {}),
        ...(kind === 'login' && aal ? { aal } : {}),
      },
    }),
  getFlow: (kind: FlowKind, id: string, signal?: AbortSignal) =>
    kratosClient.get<KratosFlow>(`/self-service/${kind}/flows`, { params: { id }, signal }),
  submitFlow: (action: string, data: Record<string, string>, signal?: AbortSignal) =>
    kratosClient.post<unknown>(kratosActionUrl(action, window.location.origin), new URLSearchParams(data), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      signal,
    }),
  getError: (id: string) => kratosClient.get<unknown>('/self-service/errors', { params: { id } }),
}
